import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css'; // We'll create this CSS file

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [currentTypeFilter, setCurrentTypeFilter] = useState('all');
  const [currentStatusFilter, setCurrentStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('Admin');
  const scrollPositionRef = useRef(0);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/submissions', { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }

      const data = await response.json();
      // Map _id to id for frontend compatibility
      const mappedData = data.map(submission => ({
        ...submission,
        id: submission._id
      }));
      setAllSubmissions(mappedData);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setError('Failed to load submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/status', { credentials: 'include' });
      const data = await response.json();

      if (!data.loggedIn) {
        console.warn("User Not LoggedIn redirecting to login page")
        navigate('/admin/login'); 
        return;
      }

      setUsername(data.username);
      loadSubmissions();
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/admin/login'); 
    }
  }, [navigate, loadSubmissions]);

  useEffect(() => {
    checkAuth();
    // Auto refresh every 30 seconds
    const interval = setInterval(loadSubmissions, 30000);
    
    return () => {
      clearInterval(interval);
    };
  }, [checkAuth, loadSubmissions]);
  
  // Save scroll position before operations
  const saveScrollPosition = () => {
    scrollPositionRef.current = window.scrollY;
  };
  
  // Restore scroll position after operations
  const restoreScrollPosition = () => {
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 100);
  };



  const markAsRead = async (type, id) => {
    try {
      saveScrollPosition();
      console.log('Marking as read:', { type, id });
      const response = await fetch(`/api/admin/submissions/${type}/${id}/read`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      console.log('Mark as read response:', result);

      if (response.ok) {
        // Update local state instead of full refresh
        setAllSubmissions(prev => 
          prev.map(s => (s.id === id || s._id === id) ? { ...s, status: 'read' } : s)
        );
        restoreScrollPosition();
      } else {
        alert('Failed to mark as read: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
      alert('Failed to mark as read: ' + error.message);
    }
  };

  const markAsUnread = async (type, id) => {
    try {
      saveScrollPosition();
      console.log('Marking as unread:', { type, id });
      const response = await fetch(`/api/admin/submissions/${type}/${id}/unread`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      console.log('Mark as unread response:', result);

      if (response.ok) {
        // Update local state instead of full refresh
        setAllSubmissions(prev => 
          prev.map(s => (s.id === id || s._id === id) ? { ...s, status: 'unread' } : s)
        );
        restoreScrollPosition();
      } else {
        alert('Failed to mark as unread: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error marking as unread:', error);
      alert('Failed to mark as unread: ' + error.message);
    }
  };

  const deleteSubmission = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      saveScrollPosition();
      console.log('Deleting submission:', { type, id });
      const response = await fetch(`/api/admin/submissions/${type}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      console.log('Delete response:', result);

      if (response.ok) {
        // Update local state instead of full refresh
        setAllSubmissions(prev => prev.filter(s => s.id !== id && s._id !== id));
        restoreScrollPosition();
      } else {
        alert('Failed to delete submission: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission: ' + error.message);
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/admin/logout', { method: 'POST', credentials: 'include' });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/admin/login');
    }
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString()}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString()}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
    }
  };

  const getFilteredSubmissions = () => {
    let filtered = allSubmissions;

    if (currentTypeFilter !== 'all') {
      filtered = filtered.filter(s => s.type === currentTypeFilter);
    }

    if (currentStatusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === currentStatusFilter);
    }

    return filtered;
  };

  const getStats = () => {
    const total = allSubmissions.length;
    const contact = allSubmissions.filter(s => s.type === 'contact').length;
    const waitlist = allSubmissions.filter(s => s.type === 'waitlist').length;
    const feedback = allSubmissions.filter(s => s.type === 'feedback').length;
    const unread = allSubmissions.filter(s => s.status === 'unread').length;

    return { total, contact, waitlist, feedback, unread };
  };

  const stats = getStats();
  const filteredSubmissions = getFilteredSubmissions();

  return (
    <div className="admin-dashboard">
      <header className="header">
        <div className="header-content">
          <div className="logo">Allyverto Admin</div>
          <div className="header-actions">
            <span>Welcome, {username}</span>
            <a href="/" className="btn btn-outline" onClick={() => setTimeout(logout, 1000)}>
              View Site
            </a>
            <button className="btn btn-primary" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-admin">
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.contact}</div>
            <div className="stat-label">Contact Forms</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.waitlist}</div>
            <div className="stat-label">Waitlist Signups</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.feedback}</div>
            <div className="stat-label">Feedback</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.unread}</div>
            <div className="stat-label">Unread</div>
          </div>
        </div>

        <div className="submissions-section">
          <div className="section-header">
            <h2 className="section-title">Submissions</h2>
            <div className="filters">
              <button 
                className={`tab-btn ${currentTypeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setCurrentTypeFilter('all')}
              >
                All
              </button>
              <button 
                className={`tab-btn ${currentTypeFilter === 'contact' ? 'active' : ''}`}
                onClick={() => setCurrentTypeFilter('contact')}
              >
                Contact
              </button>
              <button
                className={`tab-btn ${currentTypeFilter === 'waitlist' ? 'active' : ''}`}
                onClick={() => setCurrentTypeFilter('waitlist')}
              >
                Waitlist
              </button>
              <button
                className={`tab-btn ${currentTypeFilter === 'feedback' ? 'active' : ''}`}
                onClick={() => setCurrentTypeFilter('feedback')}
              >
                Feedback
              </button>
              <select 
                className="filter-select" 
                value={currentStatusFilter}
                onChange={(e) => setCurrentStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
              <button className="refresh-btn" onClick={loadSubmissions}>
                Refresh
              </button>
            </div>
          </div>

          <div className="submissions-container">
            {loading ? (
              <div className="loading">
                <div className="spinner"></div>
                <div>Loading submissions...</div>
              </div>
            ) : error ? (
              <div className="empty-state">
                <div className="empty-icon">❌</div>
                <h3>Error Loading Submissions</h3>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadSubmissions}>
                  Retry
                </button>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h3>No Submissions Found</h3>
                <p>No {currentTypeFilter === 'all' ? 'submissions' : `${currentTypeFilter} submissions`} yet.</p>
              </div>
            ) : (
              <div className="submissions-grid">
                {filteredSubmissions.map((submission) => (
                  <div key={submission.id || submission._id} className={`submission-card ${submission.status} ${submission.type}`}>
                    <div className="submission-header">
                      <div className="submission-info">
                        <div className={`submission-type type-${submission.type}`}>
                          {submission.type === 'contact' ? 'Contact Form' : submission.type === 'waitlist' ? 'Waitlist Signup' : 'Feedback'}
                        </div>
                        <div className="submission-name">
                          {escapeHtml(submission.fullname || submission.name || 'Anonymous')}
                        </div>
                        <div className="submission-email">{escapeHtml(submission.email)}</div>
                        <div className="submission-date">{formatDate(submission.timestamp)}</div>
                      </div>
                      <div className={`submission-status status-${submission.status}`}>
                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                      </div>
                    </div>

                    {submission.type === 'contact' ? (
                      <>
                        <div className="submission-subject">
                          <strong>Subject:</strong> {escapeHtml(submission.subject)}
                        </div>
                        <div
                          className="submission-message"
                          dangerouslySetInnerHTML={{
                            __html: escapeHtml(submission.message).replace(/\n/g, '<br>')
                          }}
                        />
                      </>
                    ) : submission.type === 'feedback' ? (
                      <>
                        <div className="submission-details">
                          <div className="detail-item">
                            <div className="detail-label">Category</div>
                            <div className="detail-value">{escapeHtml(submission.category || 'General Feedback')}</div>
                          </div>
                          <div className="detail-item">
                            <div className="detail-label">Email</div>
                            <div className="detail-value">{escapeHtml(submission.email || 'Not provided')}</div>
                          </div>
                        </div>
                        <div className="submission-message" style={{ whiteSpace: 'pre-wrap' }}>
                          <strong>Message:</strong>{'\n'}{submission.message}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="submission-details">
                          <div className="detail-item">
                            <div className="detail-label">Company</div>
                            <div className="detail-value">{escapeHtml(submission.company || 'Not specified')}</div>
                          </div>
                          <div className="detail-item">
                            <div className="detail-label">Interest</div>
                            <div className="detail-value">{escapeHtml(submission.interest || 'Not specified')}</div>
                          </div>
                        </div>
                        {submission.message && submission.message.trim() && submission.message !== 'No additional message' && (
                          <div className="submission-message">
                            <strong>Message:</strong><br />
                            <span 
                              dangerouslySetInnerHTML={{
                                __html: escapeHtml(submission.message).replace(/\n/g, '<br>')
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}

                    <div className="submission-actions">
                      {submission.status === 'unread' ? (
                        <button 
                          className="btn btn-success btn-small"
                          onClick={() => markAsRead(submission.type, submission.id || submission._id)}
                        >
                          Mark as Read
                        </button>
                      ) : (
                        <button 
                          className="btn btn-outline btn-small"
                          onClick={() => markAsUnread(submission.type, submission.id || submission._id)}
                        >
                          Mark as Unread
                        </button>
                      )}
                      <button 
                        className="btn btn-danger btn-small"
                        onClick={() => deleteSubmission(submission.type, submission.id || submission._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;